import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Banknote, CalendarClock, HandCoins, IdCard, Info, Pencil, Plus, Trash2, UserX, WalletCards } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'wouter';
import { MultiCheck, toggleInList } from '../components/ui/MultiCheck';
import { BranchPicker } from '../components/ui/BranchPicker';
import { Checkbox } from '../components/ui/Checkbox';
import { PhoneField } from '../components/ui/PhoneField';
import { Select } from '../components/ui/Select';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { useAuth, type Role } from '../lib/AuthContext';
import { EMPLOYMENT_HINTS, EMPLOYMENT_LABELS, shiftLabel, useBranches, useLeaveTypes, useRegions, useShifts, type EmploymentType } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type RoleAssignment = { id: string; role: Role; branchId: string | null; regionId: string | null };
type BranchAssignment = { id: string; branchId: string; assignedFrom: string; assignedTo: string | null; isRoaming: boolean; shiftId: string | null };
// KYC/bank fields are only present in the API response when the viewer is HR/Admin — the
// backend omits them entirely for a scoped manager, so they're optional here, not nullable.
type StaffDetail = {
  id: string; name: string; phone: string; email: string | null; employeeCode: string; joiningDate: string; isActive: boolean; roles: RoleAssignment[]; branchHistory: BranchAssignment[];
  designation: string | null; department: string | null;
  panNumber?: string | null; aadhaarNumber?: string | null; esicNumber?: string | null; uanNumber?: string | null; employmentType?: EmploymentType; bankAccountHolderName?: string | null; bankAccountNumber?: string | null; bankIfsc?: string | null; bankName?: string | null;
};

const ROLE_LABELS: Record<Role, string> = { staff: 'Staff', branch_head: 'Branch Head', region_head: 'Region Head', general_head: 'General Head', hr: 'HR', admin: 'Admin' };
const HR_ADMIN_TIER = new Set<Role>(['hr', 'admin']);

function Note({ children }: { children: ReactNode }) {
  return <p className="mt-3 flex items-start gap-2 rounded-xl bg-canvas px-3 py-2 text-sm text-muted"><Info size={16} className="mt-0.5 shrink-0" />{children}</p>;
}

// Basic + KYC + Bank are all edited together in one form when editing — mirrors the same
// section layout as the "Add staff" form (Basic details / KYC details / Bank account details)
// instead of three disconnected mini-forms each with their own edit toggle, which was the
// actual complaint: editing an existing person looked nothing like creating one.
function StaffProfileSection({ staff, isSelf, canEdit }: { staff: StaffDetail; isSelf: boolean; canEdit: boolean }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(staff.name);
  const [phone, setPhone] = useState(staff.phone);
  const { user } = useAuth();
  // Mirrors the server rule: only an Admin may change an HR/Admin's login number.
  const canEditPhone = !staff.roles.some((r) => HR_ADMIN_TIER.has(r.role)) || (user?.roles.includes('admin') ?? false);
  const [email, setEmail] = useState(staff.email ?? '');
  const [employeeCode, setEmployeeCode] = useState(staff.employeeCode);
  const [joiningDate, setJoiningDate] = useState(staff.joiningDate);
  const [designation, setDesignation] = useState(staff.designation ?? '');
  const [department, setDepartment] = useState(staff.department ?? '');
  const [panNumber, setPanNumber] = useState(staff.panNumber ?? '');
  const [aadhaarNumber, setAadhaarNumber] = useState(staff.aadhaarNumber ?? '');
  const [esicNumber, setEsicNumber] = useState(staff.esicNumber ?? '');
  const [uanNumber, setUanNumber] = useState(staff.uanNumber ?? '');
  const [employmentType, setEmploymentType] = useState<EmploymentType>(staff.employmentType ?? 'on_roll');
  const [bankAccountHolderName, setBankAccountHolderName] = useState(staff.bankAccountHolderName ?? '');
  const [bankAccountNumber, setBankAccountNumber] = useState(staff.bankAccountNumber ?? '');
  const [bankIfsc, setBankIfsc] = useState(staff.bankIfsc ?? '');
  const [bankName, setBankName] = useState(staff.bankName ?? '');
  const hasKyc = staff.panNumber !== undefined;

  const save = useMutation({
    mutationFn: () => api.patch(`/api/staff/${staff.id}`, {
      name, ...(phone !== staff.phone ? { phone } : {}), email: email || null, employeeCode, joiningDate, designation: designation || null, department: department || null,
      ...(hasKyc ? { panNumber: panNumber || null, aadhaarNumber: aadhaarNumber || null, esicNumber: esicNumber || null, uanNumber: uanNumber || null, employmentType, bankAccountHolderName: bankAccountHolderName || null, bankAccountNumber: bankAccountNumber || null, bankIfsc: bankIfsc || null, bankName: bankName || null } : {}),
    }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); void client.invalidateQueries({ queryKey: ['staff'] }); success(phone !== staff.phone ? 'Staff record updated — they now sign in with the new number' : 'Staff record updated'); setEditing(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not save')),
  });
  const toggleActive = useMutation({
    mutationFn: () => api.patch(`/api/staff/${staff.id}`, { isActive: !staff.isActive }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); void client.invalidateQueries({ queryKey: ['staff'] }); success(staff.isActive ? 'Staff member deactivated' : 'Staff member reactivated'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update status')),
  });

  if (editing) {
    return (
      <form className="card grid gap-4 sm:grid-cols-2 lg:col-span-2" onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}>
        <h2 className="text-sm font-bold uppercase tracking-wide text-muted sm:col-span-2">Basic details</h2>
        <label className="label">Full name<input className="field mt-1" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="label">Employee code<input className="field mt-1" required value={employeeCode} onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())} /></label>
        <label className="label">Phone
          <PhoneField className="mt-1" required value={phone} onChange={setPhone} disabled={!canEditPhone} />
          <span className="mt-1 block text-xs font-normal text-muted">
            {canEditPhone ? (phone !== staff.phone ? 'Saving signs them out everywhere; they log in again with the new number (OTP on WhatsApp).' : 'Used for login (WhatsApp OTP).') : "Only an Admin can change an HR or Admin's number."}
          </span>
        </label>
        <label className="label">Email<input type="email" className="field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="label">Joining date<input type="date" className="field mt-1" required value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} /></label>
        <label className="label">Designation<input className="field mt-1" placeholder="e.g. Delivery / Pickup Operation" value={designation} onChange={(e) => setDesignation(e.target.value)} /></label>
        <label className="label">Department<input className="field mt-1" value={department} onChange={(e) => setDepartment(e.target.value)} /></label>

        {hasKyc && (
          <>
            <h2 className="mt-2 text-sm font-bold uppercase tracking-wide text-muted sm:col-span-2">KYC details</h2>
            <label className="label">PAN number<input className="field mt-1 uppercase" placeholder="ABCDE1234F" maxLength={10} value={panNumber} onChange={(e) => setPanNumber(e.target.value.toUpperCase())} /></label>
            <label className="label">Aadhaar number<input className="field mt-1" placeholder="12-digit number" maxLength={12} inputMode="numeric" value={aadhaarNumber} onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ''))} /></label>
            <label className="label">ESIC number<input className="field mt-1" placeholder="e.g. 3713813368" value={esicNumber} onChange={(e) => setEsicNumber(e.target.value)} /></label>
            <label className="label">UAN number<input className="field mt-1" placeholder="12-digit number" maxLength={12} inputMode="numeric" value={uanNumber} onChange={(e) => setUanNumber(e.target.value.replace(/\D/g, ''))} /></label>
            <label className="label">Staff category<Select className="mt-1" value={employmentType} onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}>{(Object.keys(EMPLOYMENT_LABELS) as EmploymentType[]).map((k) => <option key={k} value={k}>{EMPLOYMENT_LABELS[k]}</option>)}</Select><span className="mt-1 block text-xs text-muted">{EMPLOYMENT_HINTS[employmentType]}</span></label>

            <h2 className="mt-2 text-sm font-bold uppercase tracking-wide text-muted sm:col-span-2">Bank account details</h2>
            <label className="label">Account holder name<input className="field mt-1" value={bankAccountHolderName} onChange={(e) => setBankAccountHolderName(e.target.value)} /></label>
            <label className="label">Bank name<input className="field mt-1" value={bankName} onChange={(e) => setBankName(e.target.value)} /></label>
            <label className="label">Account number<input className="field mt-1" inputMode="numeric" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ''))} /></label>
            <label className="label">IFSC code<input className="field mt-1 uppercase" placeholder="HDFC0001234" maxLength={11} value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase())} /></label>
          </>
        )}

        <div className="mt-2 flex items-center gap-2 sm:col-span-2">
          <button className="btn-primary" disabled={save.isPending}>Save</button>
          <button type="button" className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </form>
    );
  }

  return (
    <>
      <div className="card lg:col-span-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">{staff.name}</h2>
            <p className="mt-1 text-sm text-muted">{staff.employeeCode} • {staff.phone}{staff.email ? ` • ${staff.email}` : ''}</p>
            {(staff.designation || staff.department) && <p className="mt-1 text-sm text-muted">{[staff.designation, staff.department].filter(Boolean).join(' • ')}</p>}
            <p className="mt-1 text-sm text-muted">Joined {new Date(`${staff.joiningDate}T00:00:00`).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
            {!staff.isActive && <span className="badge mt-2 bg-red-50 text-red-700">Inactive</span>}
          </div>
          {canEdit && (
            <div className="flex shrink-0 gap-2">
              <button className="btn-secondary !px-3" aria-label="Edit staff record" onClick={() => setEditing(true)}><Pencil size={16} /></button>
              <button className="btn-secondary !px-3" aria-label={staff.isActive ? 'Deactivate' : 'Reactivate'} disabled={toggleActive.isPending} onClick={() => { if (confirm(staff.isActive ? `Deactivate ${staff.name}? This revokes their app access.` : `Reactivate ${staff.name}?`)) toggleActive.mutate(); }}>
                <UserX size={16} />
              </button>
            </div>
          )}
        </div>
        {isSelf && <Note>You can't edit your own profile or deactivate yourself. Ask another HR/Admin.</Note>}
        {!isSelf && !canEdit && <Note>Only HR/Admin can edit staff records.</Note>}
      </div>

      {hasKyc && (
        <div className="card lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <h3 className="flex items-center gap-2 font-bold"><IdCard size={18} /> KYC details</h3>
              <p className="mt-2 text-sm text-muted">PAN: {staff.panNumber ?? 'Not recorded'}</p>
              <p className="mt-1 text-sm text-muted">Aadhaar: {staff.aadhaarNumber ?? 'Not recorded'}</p>
              <p className="mt-1 text-sm text-muted">ESIC number: {staff.esicNumber ?? 'Not recorded'}</p>
              <p className="mt-1 text-sm text-muted">UAN number: {staff.uanNumber ?? 'Not recorded'}</p>
              <p className="mt-1 text-sm text-muted">Category: {EMPLOYMENT_LABELS[staff.employmentType ?? 'on_roll']}</p>
            </div>
            <div>
              <h3 className="flex items-center gap-2 font-bold"><Banknote size={18} /> Bank account</h3>
              <p className="mt-2 text-sm text-muted">{staff.bankAccountHolderName ?? 'Not recorded'}{staff.bankName ? ` • ${staff.bankName}` : ''}</p>
              <p className="mt-1 text-sm text-muted">{staff.bankAccountNumber ? `A/C ${staff.bankAccountNumber}` : 'No account number'}{staff.bankIfsc ? ` • ${staff.bankIfsc}` : ''}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RolesCard({ staff, isSelf, isAdmin, isPrivileged }: { staff: StaffDetail; isSelf: boolean; isAdmin: boolean; isPrivileged: boolean }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const branches = useBranches();
  const regions = useRegions();
  const [adding, setAdding] = useState(false);
  const [role, setRole] = useState<Role>('staff');
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [regionIds, setRegionIds] = useState<string[]>([]);

  const targetIsHrAdminTier = staff.roles.some((r) => HR_ADMIN_TIER.has(r.role));
  // Role management is HR/Admin-only server-side (POST/DELETE /staff/:id/roles both require
  // requireRoles('hr','admin')) — a branch/region head must never see an "Add role" button
  // that would just 403 when clicked.
  const canManageRoles = isPrivileged && !isSelf && (isAdmin || !targetIsHrAdminTier);
  const roleOptions = (Object.keys(ROLE_LABELS) as Role[]).filter((r) => isAdmin || !HR_ADMIN_TIER.has(r));

  const addRole = useMutation({
    mutationFn: async () => {
      if (role === 'branch_head') { for (const branchId of branchIds) await api.post(`/api/staff/${staff.id}/roles`, { role, branchId }); return; }
      if (role === 'region_head' || role === 'general_head') { for (const regionId of regionIds) await api.post(`/api/staff/${staff.id}/roles`, { role, regionId }); return; }
      await api.post(`/api/staff/${staff.id}/roles`, { role });
    },
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); success('Role added'); setAdding(false); setBranchIds([]); setRegionIds([]); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add role')),
  });
  const removeRole = useMutation({
    mutationFn: (roleId: string) => api.delete(`/api/staff/${staff.id}/roles/${roleId}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); success('Role removed'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not remove role')),
  });

  const branchName = (id: string | null) => branches.data?.find((b) => b.id === id)?.name;
  const regionName = (id: string | null) => regions.data?.find((r) => r.id === id)?.name;
  const branchesInSelectedRegions = (branches.data ?? []).filter((b) => regionIds.includes(b.regionId));

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-bold">Roles</h3>
        {canManageRoles && <button className="btn-secondary !px-3" onClick={() => setAdding(!adding)}><Plus size={16} /></button>}
      </div>
      <div className="mt-3 space-y-2">
        {staff.roles.map((r) => {
          const canRemove = canManageRoles && (isAdmin || !HR_ADMIN_TIER.has(r.role));
          return (
            <div key={r.id} className="flex items-center justify-between rounded-xl bg-canvas px-3 py-2">
              <span className="text-sm font-medium">{ROLE_LABELS[r.role]}{r.branchId ? ` — ${branchName(r.branchId) ?? 'branch'}` : ''}{r.regionId ? ` — ${regionName(r.regionId) ?? 'region'}` : ''}</span>
              {canRemove && <button className="text-muted hover:text-red-700" aria-label="Remove role" onClick={() => { if (confirm('Remove this role?')) removeRole.mutate(r.id); }}><Trash2 size={15} /></button>}
            </div>
          );
        })}
      </div>
      {isSelf && <Note>You can't manage your own roles. Ask your manager or another Admin.</Note>}
      {!isSelf && !canManageRoles && <Note>{isPrivileged ? 'Only an Admin can change roles for HR or Admin staff.' : 'Only HR/Admin can manage roles.'}</Note>}
      {adding && (
        <form className="mt-4 grid gap-3 border-t border-border pt-4" onSubmit={(e: FormEvent) => { e.preventDefault(); addRole.mutate(); }}>
          <label className="label">Role<Select className="mt-1" value={role} onChange={(e) => setRole(e.target.value as Role)}>{roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></label>
          {role === 'branch_head' && <label className="label">Branches managed (select one or more)<MultiCheck options={branches.data ?? []} selected={branchIds} onToggle={(id) => toggleInList(branchIds, setBranchIds, id)} /></label>}
          {(role === 'region_head' || role === 'general_head') && (
            <>
              <label className="label">Regions managed (select one or more){role === 'general_head' && ' — General Head sits above the Region Heads here'}<MultiCheck options={regions.data ?? []} selected={regionIds} onToggle={(id) => toggleInList(regionIds, setRegionIds, id)} /></label>
              {regionIds.length > 0 && (
                <div className="rounded-xl bg-canvas px-3 py-2 text-sm">
                  <p className="font-medium">This zone will include {branchesInSelectedRegions.length} branch{branchesInSelectedRegions.length === 1 ? '' : 'es'}:</p>
                  <p className="mt-1 text-muted">{branchesInSelectedRegions.length > 0 ? branchesInSelectedRegions.map((b) => b.name).join(', ') : 'No branches in the selected regions yet.'}</p>
                </div>
              )}
            </>
          )}
          <button className="btn-primary" disabled={addRole.isPending || (role === 'branch_head' && branchIds.length === 0) || ((role === 'region_head' || role === 'general_head') && regionIds.length === 0)}>Add role</button>
        </form>
      )}
    </div>
  );
}

function BranchHistoryCard({ staff, isSelf, canEdit }: { staff: StaffDetail; isSelf: boolean; canEdit: boolean }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const branches = useBranches();
  const [transferring, setTransferring] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [transferDate, setTransferDate] = useState(new Intl.DateTimeFormat('en-CA').format(new Date()));
  const [isRoaming, setIsRoaming] = useState(false);

  const transfer = useMutation({
    mutationFn: () => api.post(`/api/staff/${staff.id}/transfer`, { branchId, transferDate, isRoaming }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); success('Branch transfer recorded'); setTransferring(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not record transfer')),
  });
  const current = staff.branchHistory.find((a) => !a.assignedTo);
  const setRoaming = useMutation({
    mutationFn: (value: boolean) => api.patch(`/api/staff/${staff.id}/roaming`, { isRoaming: value }),
    onSuccess: (_, value) => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); success(value ? 'Marked as roaming staff' : 'Roaming turned off'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update roaming')),
  });
  const shifts = useShifts();
  const setShift = useMutation({
    mutationFn: (shiftId: string | null) => api.patch(`/api/staff/${staff.id}/shift`, { shiftId }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['staff', staff.id] }); success('Shift updated'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update shift')),
  });
  const branchName = (id: string) => branches.data?.find((b) => b.id === id)?.name ?? id;
  const shiftById = (id: string | null | undefined) => shifts.data?.find((x) => x.id === id);
  const branchDefaultShift = current ? shiftById(branches.data?.find((b) => b.id === current.branchId)?.defaultShiftId) : undefined;
  const sorted = [...staff.branchHistory].sort((a, b) => b.assignedFrom.localeCompare(a.assignedFrom));

  return (
    <div className="card">
      <div className="flex items-center justify-between"><h3 className="font-bold">Branch history</h3>{canEdit && <button className="btn-secondary !px-3" onClick={() => setTransferring(!transferring)}><ArrowLeftRight size={16} /></button>}</div>
      <div className="mt-3 space-y-2">
        {sorted.map((a) => (
          <div key={a.id} className="rounded-xl bg-canvas px-3 py-2 text-sm">
            <strong>{branchName(a.branchId)}</strong>{a.isRoaming && <span className="badge ml-2">Roaming</span>}{a.shiftId && shiftById(a.shiftId) && <span className="badge ml-2">{shiftById(a.shiftId)!.name} shift</span>}
            <p className="mt-0.5 text-muted">{a.assignedFrom} → {a.assignedTo ?? 'present'}</p>
          </div>
        ))}
        {sorted.length === 0 && <p className="text-sm text-muted">No branch assignment yet.</p>}
      </div>
      {canEdit && current && (
        <label className="mt-3 flex items-center justify-between gap-3 text-sm">
          <span>Roaming staff <span className="block text-muted">Can check in from any site, not just {branchName(current.branchId)}</span></span>
          <Switch label="Roaming staff" checked={current.isRoaming} disabled={setRoaming.isPending} onChange={(value) => setRoaming.mutate(value)} />
        </label>
      )}
      {canEdit && current && (
        <label className="label mt-3">Shift
          <Select className="mt-1" value={current.shiftId ?? ''} disabled={setShift.isPending} onChange={(e) => setShift.mutate(e.target.value || null)}>
            <option value="">Branch default{branchDefaultShift ? ` — ${shiftLabel(branchDefaultShift)}` : ' (none set)'}</option>
            {shifts.data?.filter((x) => x.isActive || x.id === current.shiftId).map((x) => <option key={x.id} value={x.id}>{shiftLabel(x)}</option>)}
          </Select>
          <span className="mt-1 block text-xs font-normal text-muted">Overnight shifts check out the next morning; the shift counts on the day it started.</span>
        </label>
      )}
      {transferring && (
        <form className="mt-4 grid gap-3 border-t border-border pt-4" onSubmit={(e: FormEvent) => { e.preventDefault(); transfer.mutate(); }}>
          <label className="label">New branch<BranchPicker required value={branchId} onChange={setBranchId} /></label>
          <label className="label">Effective from<input type="date" className="field mt-1" required value={transferDate} onChange={(e) => setTransferDate(e.target.value)} /></label>
          <Checkbox checked={isRoaming} onChange={setIsRoaming} label="Roaming staff (can check in from any site)" />
          <button className="btn-primary" disabled={transfer.isPending}>Record transfer</button>
        </form>
      )}
    </div>
  );
}

type LeaveBalance = { id: string; leaveTypeId: string; year: number; entitled: string; used: string; carriedForward: string };

// Approving a leave application checks the balance row for that user/type/year and fails with
// "Insufficient leave balance" if none exists — and nothing ever creates one automatically, so
// without this card HR has no way to grant an entitlement and every approval 409s forever.
function LeaveBalancesCard({ staff, canEdit }: { staff: StaffDetail; canEdit: boolean }) {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const leaveTypes = useLeaveTypes();
  const year = new Date().getFullYear();
  const balances = useQuery({ queryKey: ['leave-balances', staff.id, year], queryFn: async () => (await api.get<LeaveBalance[]>('/api/leave-balances', { params: { userId: staff.id, year } })).data });
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [entitled, setEntitled] = useState('');
  const [carriedForward, setCarriedForward] = useState('');

  const save = useMutation({
    mutationFn: (leaveTypeId: string) => api.post('/api/leave-balances', { userId: staff.id, leaveTypeId, year, entitled, carriedForward: carriedForward || '0' }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['leave-balances', staff.id, year] }); success('Leave balance updated'); setEditingTypeId(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update balance')),
  });

  const byType = new Map((balances.data ?? []).map((b) => [b.leaveTypeId, b]));
  const activeTypes = leaveTypes.data?.filter((t) => t.isActive) ?? [];

  function startEdit(typeId: string, defaultEntitlement: string) {
    const existing = byType.get(typeId);
    setEntitled(existing?.entitled ?? defaultEntitlement);
    setCarriedForward(existing?.carriedForward ?? '0');
    setEditingTypeId(typeId);
  }

  return (
    <div className="card lg:col-span-2">
      <h3 className="flex items-center gap-2 font-bold"><CalendarClock size={18} /> Leave balances ({year})</h3>
      <div className="mt-3 space-y-2">
        {activeTypes.map((type) => {
          const b = byType.get(type.id);
          const remaining = b ? (Number(b.entitled) + Number(b.carriedForward) - Number(b.used)).toFixed(1) : null;
          return editingTypeId === type.id ? (
            <div key={type.id} className="flex flex-wrap items-end gap-2 rounded-xl bg-canvas px-3 py-2">
              <span className="mb-2 text-sm font-medium">{type.name}</span>
              <label className="label text-xs">Entitled<input type="number" step="0.5" min="0" className="field mt-1 w-24" value={entitled} onChange={(e) => setEntitled(e.target.value)} /></label>
              <label className="label text-xs">Carried forward<input type="number" step="0.5" min="0" className="field mt-1 w-24" value={carriedForward} onChange={(e) => setCarriedForward(e.target.value)} /></label>
              <button className="btn-primary !px-3" disabled={save.isPending} onClick={() => save.mutate(type.id)}>Save</button>
              <button type="button" className="btn-secondary !px-3" onClick={() => setEditingTypeId(null)}>Cancel</button>
            </div>
          ) : (
            <div key={type.id} className="flex items-center justify-between rounded-xl bg-canvas px-3 py-2">
              <div>
                <span className="text-sm font-medium">{type.name}</span>
                <p className="text-xs text-muted">
                  {b ? `${remaining} remaining of ${b.entitled}${Number(b.carriedForward) > 0 ? ` (+${b.carriedForward} carried forward)` : ''} — ${b.used} used` : 'No balance set for this year yet'}
                </p>
              </div>
              {canEdit && <button className="btn-secondary !px-3" aria-label={`Set ${type.name} balance`} onClick={() => startEdit(type.id, type.yearlyEntitlement)}><Pencil size={14} /></button>}
            </div>
          );
        })}
        {activeTypes.length === 0 && <p className="text-sm text-muted">No leave types configured yet.</p>}
      </div>
    </div>
  );
}

export function StaffDetailPage({ id }: { id: string }) {
  const { user } = useAuth();
  const query = useQuery({ queryKey: ['staff', id], queryFn: async () => (await api.get<StaffDetail>(`/api/staff/${id}`)).data });
  if (query.isLoading) return <Loader />;
  if (!query.data) return <div className="card text-center text-muted">Staff member not found.</div>;
  const staff = query.data;
  const currentUserId = user?.id ?? user?.sub;
  const isSelf = staff.id === currentUserId;
  const isAdmin = user?.roles.includes('admin') ?? false;
  const isPrivileged = user?.roles.some((r) => r === 'hr' || r === 'admin') ?? false;
  const canAccessStaffDirectory = user?.roles.some((r) => r === 'branch_head' || r === 'region_head' || isPrivileged) ?? false;

  return (
    <>
      {canAccessStaffDirectory && <Link href="/staff" className="mb-4 inline-block text-sm font-medium text-brand-700">← Back to staff</Link>}
      <div className="grid gap-4 lg:grid-cols-2">
        <StaffProfileSection staff={staff} isSelf={isSelf} canEdit={isPrivileged && !isSelf} />
        <RolesCard staff={staff} isSelf={isSelf} isAdmin={isAdmin} isPrivileged={isPrivileged} />
        <BranchHistoryCard staff={staff} isSelf={isSelf} canEdit={isPrivileged && !isSelf} />
        <LeaveBalancesCard staff={staff} canEdit={isPrivileged && !isSelf} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {/* Loans/salary are HR/Admin-only for someone else's record — branch/region heads never see compensation data (SoW §5.2-5.3), same rule the backend enforces. */}
        {(isPrivileged || isSelf) && <Link href={`/loans?userId=${staff.id}`} className="btn-secondary"><HandCoins size={16} /> Loans &amp; advances</Link>}
        {isPrivileged && <Link href={`/salary?userId=${staff.id}`} className="btn-secondary"><WalletCards size={16} /> Salary</Link>}
      </div>
    </>
  );
}
